import {
  BLACK,
  Chess,
  Color,
  Move,
  MoveShort,
  PieceSymbol,
  WHITE,
} from "@chesshotel/chess";

export type GameplayMove = {
  color?: Color;
  from?: string;
  to?: string;
  piece?: PieceSymbol;
  captured?: PieceSymbol;
  promotion?: string;
  flags?: string;
  fen?: string;
  san?: string;
};

export type CapturedPiece = {
  piece: PieceSymbol;
  color: Color;
};

export type CapturedMaterial = {
  white: {
    capturedPieces: CapturedPiece[];
    materialAdvantage: number;
  };
  black: {
    capturedPieces: CapturedPiece[];
    materialAdvantage: number;
  };
};

export const PIECE_VALUES: Record<PieceSymbol, number> = {
  p: 1,
  n: 3,
  b: 3,
  r: 5,
  q: 9,
  k: 0,
};

function normalizePromotion(promotion?: string): PieceSymbol | undefined {
  if (!promotion) return undefined;
  return promotion.toLowerCase() as PieceSymbol;
}

function formatCoordinateMove(move: GameplayMove) {
  if (!move?.from || !move?.to) return "";
  return `${move.from}${move.to}${move.promotion ?? ""}`;
}

function toMoveInput(move: GameplayMove): MoveShort | null {
  if (!move?.from || !move?.to) return null;
  return {
    from: move.from as MoveShort["from"],
    to: move.to as MoveShort["to"],
    promotion: normalizePromotion(move.promotion),
  };
}

export function buildGameplayMoveRecords(
  moves: GameplayMove[] = [],
  initialFen?: string
) {
  let chess: Chess | null = null;
  try {
    chess = new Chess(initialFen);
  } catch {
    chess = null;
  }

  return moves.map((move, index) => {
    const input = toMoveInput(move);
    let playedMove: Move | null = null;

    if (chess && input) {
      try {
        playedMove = chess.move(input);
      } catch {
        playedMove = null;
      }
    }

    const color =
      playedMove?.color ?? move.color ?? (index % 2 === 0 ? WHITE : BLACK);
    const san = playedMove?.san ?? move.san ?? formatCoordinateMove(move);

    return {
      ...move,
      color,
      san,
      piece: playedMove?.piece ?? move.piece,
      captured: playedMove?.captured ?? move.captured,
      promotion: playedMove?.promotion ?? normalizePromotion(move.promotion),
      from: playedMove?.from ?? move.from,
      to: playedMove?.to ?? move.to,
    };
  });
}

export function getCapturedMaterial(
  moves: GameplayMove[] = [],
  initialFen?: string,
  positionIndex = moves.length
): CapturedMaterial {
  const capturedByWhite: CapturedPiece[] = [];
  const capturedByBlack: CapturedPiece[] = [];
  const records = buildGameplayMoveRecords(
    moves.slice(0, Math.max(0, positionIndex)),
    initialFen
  );

  records.forEach((move) => {
    if (!move.captured) return;

    const capturedPiece = move.captured.toLowerCase() as PieceSymbol;
    const moverColor = move.color as Color;
    const capturedColor: Color = moverColor === WHITE ? BLACK : WHITE;
    const captured = { piece: capturedPiece, color: capturedColor };

    if (moverColor === WHITE) {
      capturedByWhite.push(captured);
    } else {
      capturedByBlack.push(captured);
    }
  });

  const whiteMaterial = capturedByWhite.reduce(
    (total, captured) => total + PIECE_VALUES[captured.piece],
    0
  );
  const blackMaterial = capturedByBlack.reduce(
    (total, captured) => total + PIECE_VALUES[captured.piece],
    0
  );
  const materialDiff = whiteMaterial - blackMaterial;

  return {
    white: {
      capturedPieces: capturedByWhite,
      materialAdvantage: materialDiff > 0 ? materialDiff : 0,
    },
    black: {
      capturedPieces: capturedByBlack,
      materialAdvantage: materialDiff < 0 ? Math.abs(materialDiff) : 0,
    },
  };
}

export function isMoveInDests(move: MoveShort, dests: Record<string, string[]>) {
  const moves = dests?.[move.from];
  return Array.isArray(moves) && moves.includes(move.to);
}

export function withDefaultPromotion(
  move: MoveShort,
  playingWhite: boolean
): MoveShort {
  if (move.promotion) return move;
  const promotionRank = playingWhite ? "8" : "1";

  if (move.to?.[1] !== promotionRank) return move;

  return {
    ...move,
    promotion: "q",
  };
}

export function createMoveWithFen(move: MoveShort, fen: string) {
  try {
    const playedMove = new Chess(fen).move(move);
    return {
      from: playedMove.from,
      to: playedMove.to,
      promotion: playedMove.promotion,
      fen: playedMove.after,
    };
  } catch {
    return null;
  }
}
